import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { MoonLoader } from 'react-spinners';
import ReactFlow, {
  addEdge,
  Background,
  MiniMap,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import { Panel } from 'reactflow';
import { FiZoomIn, FiZoomOut } from 'react-icons/fi';
import { TbArrowsMaximize } from 'react-icons/tb';
import { FiX, FiPlus, FiEdit } from 'react-icons/fi';
import { AiOutlineClear } from 'react-icons/ai';
import { Globe } from 'lucide-react';
import 'reactflow/dist/style.css';
import { BiUndo, BiRedo } from 'react-icons/bi';
import { FaToolbox } from 'react-icons/fa';

import {
  WORKFLOW_API_URL,
  saveWorkflowDraft,
  getWorkflowDraft,
  createWorkflow,
  modifyWorkflow,
  getSpecificWorkflow,
} from '../../services/workflowApi';
import ActionNodeModal from './ActionNodeModal';
import TriggerNodeModal from './TriggerNodeModal';
import TriggerNode from './TriggerNode';
import ActionNode from './ActionNode';
import ConditionalNode from './ConditionalNode';
import ConditionalNodeModal from './ConditionalNodeModal';
import DataNodeModal from './DataNodeModal';
import DataNode from './DataNode';
import AgentNode from './AgentNode';
import AgentNodeModal from './AgentNodeModal';
import { DataState } from '../../context/DataProvider';
import TitleDescriptionModal from './TitleDescriptionModal';
import { integrationData } from '../../utils/constants';
import IntegrationsModal from './IntegrationsModal';
import DataSplitterNode from './DataSplitterNode';
import DataSplitterNodeModal from './DataSplitterNodeModal';
import DataArrayNode from './DataArrayNode';
import DataArrayNodeModal from './DataArrayNodeModal';
import ToolInputNode from './ToolInputNode';
import ToolInputNodeModal from './ToolInputNodeModal';
import RestructureNode from './RestructureNode';
import RestructureNodeModal from './RestructureNodeModal';
import ToolOutputNode from './ToolOutputNode';
import ToolOutputNodeModal from './ToolOutputNodeModal';
import CustomAgentNode from './CustomAgentNode';
import CustomAgentNodeModal from './CustomAgentNodeModal';
import OperationsNode from './OperationsNode';
import OperationsNodeModal from './OperationsNodeModal';
import UnwrapArrayNode from './UnwrapArrayNode';
import UnwrapArrayNodeModal from './UnwrapArrayNodeModal';
import AddNodeModal from './AddNodeModal';
import SchemaBuilderModal from '../NewWorkflow/modals/SchemaBuilderModal';
import { generateInitialStateData } from '../NewWorkflow/utils/schemaUtils';
import LoopBreakNode from './LoopBreakNode';
import LoopBreakNodeModal from './LoopBreakNodeModal';
import StructuralConditionalNode from './StructuralConditionalNode';
import StructuralConditionalNodeModal from './StructuralConditionalNodeModal';

const initialNodes = [];
const initialEdges = [];

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  conditional: ConditionalNode, // Add this
  data: DataNode, // Add DataNode here
  dataSplitter: DataSplitterNode, // New custom node type
  dataArray: DataArrayNode,
  agent: AgentNode, // Add AgentNode here
  // --- NEW NODE TYPES ---
  toolInput: ToolInputNode,
  restructure: RestructureNode,
  toolOutput: ToolOutputNode,
  customAgent: CustomAgentNode,
  operations: OperationsNode,
  unwrapArray: UnwrapArrayNode,
  loopBreak: LoopBreakNode,
  structuralConditional: StructuralConditionalNode,
};

const WorkflowCanvas = ({ closeModal, mode = 'create', workflowId = null }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { userGoogleId, setNeedBotFetch } = DataState();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add' or 'edit' or 'integrate'
  const [nodeType, setNodeType] = useState(''); // 'trigger' or 'action'
  const [selectedNode, setSelectedNode] = useState(null);
  const [appEventData, setAppEventData] = useState([]);
  const [appData, setAppData] = useState([]);
  const [workflowTitle, setWorkflowTitle] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [nodeIdCounter, setNodeIdCounter] = useState(1); // Counter for node IDs
  // const [integrationsModalOpen, setIntegrationsModalOpen] = useState(false);
  const [selectedIntegrationAppOptions, setSelectedIntegrationAppOptions] =
    useState([]);
  // const [selectedDataSources, setSelectedDataSources] = useState([]);
  const [selectedDataSourceFiles, setSelectedDataSourceFiles] = useState([]);

  const [agentsData, setAgentsData] = useState([]); // State for agents list

  // 2. Add state for the new modal
  const [isAddNodeModalOpen, setIsAddNodeModalOpen] = useState(false);

  // --- NEW: GLOBAL STATE MANAGEMENT ---
  const [globalState, setGlobalState] = useState({
    fields: [],
    modelName: 'globalState',
  });
  const [isGlobalStateEditorOpen, setIsGlobalStateEditorOpen] = useState(false);

  // This new handler will be passed to the AddNodeModal.
  // It simply calls the existing handleAddNode function.
  const handleSelectNodeType = (nodeType) => {
    handleAddNode(nodeType);
  };

  // History management
  // eslint-disable-next-line
  const [history, setHistory] = useState({
    past: [],
    present: { nodes: [], edges: [] },
    future: [],
  });

  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false);

  const [customNodesDropdownOpen, setCustomNodesDropdownOpen] = useState(false);

  const [integrationLoading, setIntegrationLoading] = useState(true);
  const reactFlowInstance = useReactFlow();

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const toggleCustomNodesDropdown = () => {
    setCustomNodesDropdownOpen((prev) => !prev);
  };

  const handleCustomNodesSelect = (selectedType) => {
    // For now, we only have one custom node type:
    setNodeType(selectedType); // e.g., "dataSplitter"
    setModalType('add');
    setModalOpen(true);
    setCustomNodesDropdownOpen(false);
  };

  // Initialize present state
  useEffect(() => {
    setHistory((prev) => ({
      ...prev,
      present: { nodes, edges },
    }));
    // eslint-disable-next-line
  }, []);

  // Save state to history when non-drag changes occur
  useEffect(() => {
    if (!isUndoRedoAction) {
      setHistory((prev) => ({
        past: [...prev.past, prev.present],
        present: { nodes, edges },
        future: [],
      }));
    }
    setIsUndoRedoAction(false);
    // eslint-disable-next-line
  }, [nodes.length, edges.length]); // Only trigger on structural changes

  const handleUndo = useCallback(() => {
    setHistory((prev) => {
      if (prev.past.length === 0) return prev;

      const newPresent = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);

      setIsUndoRedoAction(true);
      setNodes(newPresent.nodes);
      setEdges(newPresent.edges);

      return {
        past: newPast,
        present: newPresent,
        future: [prev.present, ...prev.future],
      };
    });
  }, [setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    setHistory((prev) => {
      if (prev.future.length === 0) return prev;

      const newPresent = prev.future[0];
      const newFuture = prev.future.slice(1);

      setIsUndoRedoAction(true);
      setNodes(newPresent.nodes);
      setEdges(newPresent.edges);

      return {
        past: [...prev.past, prev.present],
        present: newPresent,
        future: newFuture,
      };
    });
  }, [setNodes, setEdges]);

  const fetchAppsandEventsData = async () => {
    try {
      const response = await fetch(
        `${WORKFLOW_API_URL}/canvas/appsAndTriggers`,
        {
          method: 'POST', // Use the appropriate HTTP method (POST, PUT, etc.)
          headers: {
            'Content-Type': 'application/json', // Specify that you're sending JSON
            Authorization: `Bearer ${userGoogleId}`,
          },
          credentials: 'include',
          body: JSON.stringify({ apps: selectedIntegrationAppOptions }), // Convert the payload to a JSON string
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status}`);
      }

      const data = await response.json();
      setAppData(data.appsData || []); // Update appData with the fetched data
      setAppEventData(data.appsEventsData || []);
      setAgentsData(data.agents || []); // <<< Store agents data
      // fetchJiraProjects(data.appsEventsData || []);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : JSON.stringify(error));
    }
  };
  useEffect(() => {
    // Fetch the app data from the API endpoint

    const setStartingCanvas = async () => {
      try {
        setIsLoading(true);
        setIntegrationLoading(true);
        let data = null;
        if (mode === 'edit' && workflowId) {
          data = await getSpecificWorkflow(workflowId, userGoogleId);
        } else {
          data = await getWorkflowDraft(userGoogleId);
        }
        if (data) {
          setEdges(
            (data.edges || []).map((edge) => {
              return {
                ...edge,
                animated: true,
                markerEnd: { type: 'arrow' },
                type: 'smoothstep',
              };
            }),
          );
          const nodesWithDataSourcesAdded = (data.nodes || []).map((node) => {
            if (node.type !== 'data') {
              return node;
            }
            const data_source_id = node.data.selectedSource || '';
            const selected_data_source = (data.data_sources || []).find(
              (source) => data_source_id.file_id === source.file_id,
            );
            return {
              ...node,
              data: { ...node.data, selectedSource: selected_data_source },
            };
          });

          // --- NEW: Set the global state from the loaded data ---
          if (data.globalState && data.globalState.fields) {
            setGlobalState(data.globalState);
          }

          setNodes(nodesWithDataSourcesAdded || []);
          setWorkflowTitle(data.title || '');
          setWorkflowDescription(data.description || '');
          setSelectedIntegrationAppOptions(data.apps_expanded || []);
          setSelectedDataSourceFiles(data.data_sources || []);

          // Find the maximum ID in the fetched nodes
          const maxNodeId = (data.nodes || []).reduce((max, node) => {
            const nodeId = parseInt(node.id, 10); // Assuming node.id is a number or can be parsed to a number
            return nodeId > max ? nodeId : max;
          }, 0);

          // Set the next NodeId state to be maxNodeId + 1
          setNodeIdCounter(maxNodeId + 1);
        }
      } catch (error) {
        toast.error(typeof error === 'string' ? error : JSON.stringify(error));
      } finally {
        setIntegrationLoading(false);
        setIsLoading(false);
      }
    };

    setStartingCanvas();
    // eslint-disable-next-line
  }, []); // Empty dependency array to call this effect only once on component mount

  useEffect(() => {
    fetchAppsandEventsData();
    // eslint-disable-next-line
  }, [selectedIntegrationAppOptions]);

  const handleAddNode = (type) => {
    setNodeType(type);
    setModalType('add');
    setModalOpen(true);
  };

  const handleNodeClick = (event, node) => {
    // Compute input nodes by filtering the edges array
    const inputNodes = edges
      .filter((edge) => edge.target === node.id)
      .map((edge) => nodes.find((n) => n.id === edge.source))
      .filter(Boolean); // Remove any undefined values

    setSelectedNode({ ...node, inputNodes }); // Deep copy of node data
    setNodeType(node.type);
    setModalType('edit');
    setModalOpen(true);
  };

  const handleIntegrationsButtonClick = () => {
    setNodeType(null);
    setModalType('integrate');
    setModalOpen(true);
  };

  const handleSaveNode = (nodeData) => {
    if (modalType === 'add') {
      // Calculate the center position of the current view
      const centerX = reactFlowInstance.screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      }).x;

      const centerY = reactFlowInstance.screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      }).y;
      const newNode = {
        id: `${nodeIdCounter}`, // Generate a unique ID
        // position: { x: Math.random() * 400, y: Math.random() * 400 },
        position: { x: centerX, y: centerY },
        data: { ...nodeData.data }, // Ensure unique data for each node
        type: nodeType,
      };
      setNodes((nds) => [...nds, newNode]);
      setNodeIdCounter((prev) => prev + 1); // Increment the counter
    } else if (modalType === 'edit') {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeData.id
            ? { ...node, data: { ...nodeData.data } }
            : node,
        ),
      );
    }
    setModalOpen(false);
  };

  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) =>
      eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
    );
    setModalOpen(false);
  };

  const handleConnect = (params) => {
    if (params.source === params.target) {
      toast.error('Cannot connect a node to itself.'); // Show a warning or handle it in your UI
      return; // Do not add the edge
    }
    // Create the edge ID using source and target
    // const edgeId = `e${params.source}-${params.target}`;
    // Create a unique edge ID by including the handle IDs (e.g., source and target positions) to differentiate
    const edgeId = `e-${params.source}-${params.sourceHandle || 'default'}-${
      params.target
    }-${params.targetHandle || 'default'}`;

    setEdges((eds) =>
      addEdge(
        {
          ...params,
          id: edgeId,
          animated: true,
          markerEnd: { type: 'arrow' },
          type: 'smoothstep',
        },
        eds,
      ),
    );
  };

  const handleEdgeClick = (event, edge) => {
    event.stopPropagation(); // Prevent triggering other click events
    setEdges((eds) => eds.filter((e) => e.id !== edge.id)); // Remove the clicked edge
  };

  const handleClearCanvas = () => {
    setEdges(initialEdges);
    setNodes(initialNodes);
    setNodeIdCounter(1);
  };

  const filterNodeData = (nodesLocal) => {
    return nodesLocal.map((node) => ({
      id: node.id,
      type: node.type,
      position: node.position,
      data:
        node.type !== 'data'
          ? node.data
          : {
              ...node.data,
              selectedSources: (node.data.selectedSources || []).map(
                (source) => source.file_id,
              ),
            }, // Keep this if your backend requires it
    }));
  };

  const filterEdgeData = (edgesLocal) => {
    return edgesLocal.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle || null,
      targetHandle: edge.targetHandle || null,
    }));
  };
  const selectedDataSourceFileIds = selectedDataSourceFiles.map(
    (file) => file.file_id,
  );

  const handleCloseCanvas = () => {
    // Filter and extract only the required fields for nodes
    const filteredNodes = filterNodeData(nodes);
    // Filter and extract only the required fields for edges
    const filteredEdges = filterEdgeData(edges);

    const func = async () => {
      try {
        if (!isSaving) {
          setIsClosing(true);
        }
        await saveWorkflowDraft(
          userGoogleId,
          filteredNodes,
          filteredEdges,
          workflowTitle,
          workflowDescription,
          selectedIntegrationAppOptions,
          selectedDataSourceFileIds,
        );
        closeModal();
      } catch (err) {
        toast.error(typeof err === 'string' ? err : JSON.stringify(err));
      } finally {
        setIsClosing(false);
      }
    };
    func();
  };
  const handleSaveTitleDescription = (title, description) => {
    setWorkflowTitle(title);
    setWorkflowDescription(description);
    setTitleModalOpen(false);
  };

  const handleSaveGlobalState = ({ fields, modelName }) => {
    setGlobalState({ fields, modelName });
    setIsGlobalStateEditorOpen(false);
    toast.success('Global State schema saved!');
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const filteredNodes = filterNodeData(nodes);

      // Filter and extract only the required fields for edges
      const filteredEdges = filterEdgeData(edges);

      // --- NEW: Generate the initial global_state data object ---
      const global_state_data = generateInitialStateData(globalState.fields);

      if (mode === 'create') {
        await createWorkflow(
          userGoogleId,
          filteredNodes,
          filteredEdges,
          workflowTitle,
          workflowDescription,
          selectedIntegrationAppOptions,
          selectedDataSourceFileIds,
          globalState, // <-- Pass globalState
          global_state_data, // The initial data object { key: value }
        );
        toast.success('Workflow created successfully!');
        setNeedBotFetch(true); // Trigger bot fetch to update the list with the new workflow
      } else if (mode === 'edit' && workflowId) {
        await modifyWorkflow(
          userGoogleId,
          workflowId,
          filteredNodes,
          filteredEdges,
          workflowTitle,
          workflowDescription,
          selectedIntegrationAppOptions,
          selectedDataSourceFileIds,
          globalState, // <-- Pass globalState
          global_state_data, // The initial data object { key: value }
        );
        toast.success('Workflow updated successfully!');
        setNeedBotFetch(true); // Trigger bot fetch to update the list with the updated workflow
      }
      handleCloseCanvas();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : JSON.stringify(error));
    } finally {
      setIsSaving(false);
    }
  };
  useEffect(() => {
    if (!integrationLoading && selectedIntegrationAppOptions.length === 0) {
      handleIntegrationsButtonClick();
    }
    // eslint-disable-next-line
  }, [selectedIntegrationAppOptions]);

  return (
    // <ReactFlowProvider>
    <div className="fixed inset-0 z-999 flex  flex-col bg-zinc-900">
      {/* FULL-SCREEN LOADING OVERLAY */}
      {isSaving && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center space-y-4 bg-black bg-opacity-75">
          <MoonLoader size={64} color="#fff" />
          <p className="text-sm text-white">
            {mode === 'create' ? 'Creating Workflow...' : 'Saving changes...'}
          </p>
        </div>
      )}
      {isLoading && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center space-y-4 bg-black bg-opacity-75">
          <MoonLoader size={64} color="#fff" />
          <p className="text-sm text-white">
            {mode === 'create' ? 'Loading draft...' : 'Loading workflow...'}
          </p>
        </div>
      )}
      {isClosing && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center space-y-4 bg-black bg-opacity-75">
          <MoonLoader size={64} color="#fff" />
          <p className="text-sm text-white">{'Saving Draft and Closing...'}</p>
        </div>
      )}

      <WorkflowHeader
        workflowTitle={workflowTitle}
        onTitleEdit={() => setTitleModalOpen(true)}
        // onAddTrigger={() => handleAddNode("trigger")}
        // onAddAction={() => handleAddNode("action")}
        // onAddConditional={() => handleAddNode("conditional")}
        // onAddData={() => handleAddNode("data")}
        // onAddAgent={() => handleAddNode("agent")} // Add prop for adding agent
        // // --- NEW PROPS FOR HEADER BUTTONS ---
        // onAddToolInput={() => handleAddNode("toolInput")}
        // onAddRestructure={() => handleAddNode("restructure")}
        // onAddToolOutput={() => handleAddNode("toolOutput")}
        // onAddCustomAgent={() => handleAddNode("customAgent")} // New
        // onAddOperations={() => handleAddNode("operations")} // New
        // onAddUnwrapArray={() => handleAddNode("unwrapArray")} // New
        onOpenAddNodeModal={() => setIsAddNodeModalOpen(true)} // <-- Pass the function to open the modal
        onEditGlobalState={() => setIsGlobalStateEditorOpen(true)}
        onAddAppIntegration={handleIntegrationsButtonClick}
        onClear={handleClearCanvas}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onClose={handleCloseCanvas}
        handleSaveChanges={handleSaveChanges}
        mode={mode}
        disabled={isSaving || isLoading || isClosing}
        onCustomNodesSelect={handleCustomNodesSelect}
        toggleCustomNodesDropdown={toggleCustomNodesDropdown}
        customNodesDropdownOpen={customNodesDropdownOpen}
      />

      <div className="flex-grow">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodesDraggable={!isSaving && !isLoading && !isClosing}
          nodesConnectable={!isSaving && !isLoading && !isClosing}
          elementsSelectable={!isSaving && !isLoading && !isClosing}
          onConnect={handleConnect}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick} // Add the edge click handler here
          fitView
          nodeTypes={nodeTypes}
        >
          <Background />
          <CustomControls />
          <MiniMap
            position="bottom-left"
            style={{
              backgroundColor: '#18181b', // zinc-900
              border: '1px solid #3f3f46', // zinc-700
              borderRadius: '6px',
            }}
            nodeColor="#52525b" // zinc-600
            maskColor="rgba(24, 24, 27, 0.6)" // zinc-900 with opacity
          />
        </ReactFlow>
      </div>

      {modalOpen && nodeType === 'action' && (
        <ActionNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          appData={appData}
        />
      )}

      {modalOpen && nodeType === 'trigger' && (
        <TriggerNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          appEventData={appEventData}
        />
      )}
      {/* // Add the Conditional Node Modal */}
      {modalOpen && nodeType === 'conditional' && (
        <ConditionalNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
        />
      )}
      {/* Modals for all node types */}
      {modalOpen && nodeType === 'data' && (
        <DataNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          dataSources={selectedDataSourceFiles}
        />
      )}
      {modalOpen && nodeType === 'agent' && (
        <AgentNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          agentsList={agentsData} // Pass the fetched agents data
        />
      )}
      {/* --- NEW MODALS --- */}
      {modalOpen && nodeType === 'toolInput' && (
        <ToolInputNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          globalState={globalState} // Pass the global state
        />
      )}

      {modalOpen && nodeType === 'restructure' && (
        <RestructureNodeModal
          mode={modalType}
          // For restructure node, we need to pass the full node, all nodes, and all edges
          node={
            modalType === 'edit'
              ? selectedNode
              : {
                  id: `restructure-${nodeIdCounter}`, // Temporary ID for add mode
                  data: {
                    outputFields: [],
                    mappings: {},
                    jsonOutputSchema: {
                      title: 'Output',
                      type: 'object',
                      properties: {},
                    },
                  },
                }
          }
          nodes={nodes}
          edges={edges}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          globalState={globalState} // Pass the global state
        />
      )}
      {modalOpen && nodeType === 'toolOutput' && (
        <ToolOutputNodeModal
          mode={modalType}
          node={
            modalType === 'edit'
              ? selectedNode
              : {
                  id: `toolOutput-${nodeIdCounter}`,
                  data: {
                    outputFields: [],
                    mappings: {},
                    jsonOutputSchema: {
                      title: 'Output',
                      type: 'object',
                      properties: {},
                    },
                  },
                }
          }
          nodes={nodes}
          edges={edges}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          globalState={globalState} // Pass the global state
        />
      )}
      {/* --- NEW MODAL FOR CUSTOM AGENT --- */}
      {modalOpen && nodeType === 'customAgent' && (
        <CustomAgentNodeModal
          mode={modalType}
          node={
            modalType === 'edit'
              ? selectedNode
              : {
                  id: `customAgent-${nodeIdCounter}`,
                  data: {}, // Modal will use its internal defaults
                }
          }
          nodes={nodes}
          edges={edges}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          globalState={globalState} // Pass the global state
        />
      )}
      {/* --- NEW MODAL FOR OPERATIONS --- */}
      {modalOpen && nodeType === 'operations' && (
        <OperationsNodeModal
          mode={modalType}
          node={
            modalType === 'edit'
              ? selectedNode
              : {
                  id: `operations-${nodeIdCounter}`,
                  data: { operations: [] }, // Default data
                }
          }
          nodes={nodes}
          edges={edges}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
          globalState={globalState} // Pass the global state
        />
      )}
      {/* --- NEW MODAL FOR UNWRAP ARRAY --- */}
      {modalOpen && nodeType === 'unwrapArray' && (
        <UnwrapArrayNodeModal
          mode={modalType}
          node={
            modalType === 'edit'
              ? selectedNode
              : {
                  id: `unwrapArray-${nodeIdCounter}`,
                  data: {}, // Modal will use its internal defaults
                }
          }
          nodes={nodes}
          edges={edges}
          onDelete={handleDeleteNode}
          onSave={handleSaveNode}
          onClose={() => setModalOpen(false)}
          globalState={globalState} // Pass the global state
        />
      )}
      {modalOpen && nodeType === 'structuralConditional' && (
        <StructuralConditionalNodeModal
          mode={modalType}
          node={
            modalType === 'edit'
              ? selectedNode
              : {
                  id: `structuralConditional-${nodeIdCounter}`,
                  data: {}, // Modal will use its internal defaults
                }
          }
          nodes={nodes}
          edges={edges}
          onDelete={handleDeleteNode}
          onSave={handleSaveNode}
          onClose={() => setModalOpen(false)}
          globalState={globalState} // Pass the global state
        />
      )}
      {/* --- NEW MODAL FOR LOOP BREAK --- */}
      {modalOpen && nodeType === 'loopBreak' && (
        <LoopBreakNodeModal
          mode={modalType}
          node={modalType === 'edit' ? selectedNode : { data: {} }}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
        />
      )}

      {modalOpen && modalType === 'integrate' && (
        <IntegrationsModal
          integrationData={integrationData}
          selectedDataSourceFiles={selectedDataSourceFiles}
          setSelectedDataSourceFiles={setSelectedDataSourceFiles}
          selectedIntegrationAppOptions={selectedIntegrationAppOptions}
          setSelectedIntegrationAppOptions={setSelectedIntegrationAppOptions}
          onClose={() => setModalOpen(false)}
        />
      )}
      {titleModalOpen && (
        <TitleDescriptionModal
          initialTitle={workflowTitle}
          initialDescription={workflowDescription}
          onClose={() => setTitleModalOpen(false)}
          onSave={handleSaveTitleDescription}
        />
      )}

      {modalOpen && nodeType === 'dataSplitter' && (
        <DataSplitterNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
        />
      )}
      {modalOpen && nodeType === 'dataArray' && (
        <DataArrayNodeModal
          mode={modalType}
          nodeData={modalType === 'edit' ? selectedNode : null}
          onSave={handleSaveNode}
          onDelete={handleDeleteNode}
          onClose={() => setModalOpen(false)}
        />
      )}

      {/* RENDER THE GLOBAL STATE SCHEMA BUILDER */}
      <SchemaBuilderModal
        isOpen={isGlobalStateEditorOpen}
        onClose={() => setIsGlobalStateEditorOpen(false)}
        onSave={handleSaveGlobalState}
        initialFields={globalState.fields}
        initialModelName={'globalState'}
        context={'globalState'} // You can use this prop to customize the modal for global state
      />

      {/* 4. Render the new AddNodeModal */}
      <AddNodeModal
        isOpen={isAddNodeModalOpen}
        onClose={() => setIsAddNodeModalOpen(false)}
        onSelectNode={handleSelectNodeType}
      />
    </div>
    // </ReactFlowProvider>
  );
};

export default WorkflowCanvas;

const CustomControls = () => {
  const { zoomIn: flowZoomIn, zoomOut: flowZoomOut, fitView } = useReactFlow();

  const handleZoomIn = useCallback(() => {
    flowZoomIn({ duration: 300 });
  }, [flowZoomIn]);

  const handleZoomOut = useCallback(() => {
    flowZoomOut({ duration: 300 });
  }, [flowZoomOut]);

  const handleFitView = useCallback(() => {
    fitView({ duration: 500, padding: 0.1 });
  }, [fitView]);

  return (
    <Panel position="bottom-right" className="m-6">
      <div className="flex flex-col gap-1 rounded-md border border-zinc-700 bg-zinc-800 p-1 shadow-lg">
        <button
          onClick={handleZoomIn}
          className="rounded p-2 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
          title="Zoom In"
        >
          <FiZoomIn size={18} />
        </button>

        <button
          onClick={handleZoomOut}
          className="rounded p-2 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
          title="Zoom Out"
        >
          <FiZoomOut size={18} />
        </button>

        <div className="my-1 h-px w-full bg-zinc-700"></div>

        <button
          onClick={handleFitView}
          className="rounded p-2 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
          title="Fit View"
        >
          <TbArrowsMaximize size={18} />
        </button>
      </div>
    </Panel>
  );
};

const WorkflowHeader = ({
  workflowTitle,
  onTitleEdit,
  disabled = false,
  onOpenAddNodeModal, // <-- New prop to open the modal
  onEditGlobalState,
  // onAddTrigger,
  // onAddAction,
  // onAddConditional,
  // onAddData,
  // onAddAgent,
  // onAddToolInput,
  // onAddRestructure,
  // onAddToolOutput,
  // onAddCustomAgent,
  // onAddOperations,
  // onAddUnwrapArray,
  onAddAppIntegration,
  onUndo,
  onRedo,
  onClose,
  onClear,
  handleSaveChanges,
  mode,
  handleCustomNodesButtonClick,
  onCustomNodesSelect,
  toggleCustomNodesDropdown,
  customNodesDropdownOpen,
}) => {
  return (
    <header className="flex items-center justify-between border-b border-zinc-700 bg-zinc-900 px-4 py-2 text-zinc-100">
      {/* Left section with title and edit */}
      <div className="flex items-center space-x-4">
        <h1 className="max-w-32 truncate text-ellipsis text-xl font-semibold">
          {workflowTitle || 'Untitled Workflow'}
        </h1>
        <button
          onClick={onTitleEdit}
          disabled={disabled}
          className={`rounded-full p-1 transition-colors hover:bg-zinc-700 ${
            disabled ? 'cursor-not-allowed opacity-50' : ''
          }`}
        >
          <FiEdit className="h-4 w-4" />
        </button>
      </div>

      {/* Center section with add node buttons */}
      {/* <div className="flex-1 flex items-center justify-center space-x-3 ">
        <button
          onClick={onAddTrigger}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <FiPlus className="w-4 h-4 mr-2" />
          <span>Trigger</span>
        </button>

        <button
          onClick={onAddAction}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <FiPlus className="w-4 h-4 mr-2" />
          <span>Action</span>
        </button>

        <button
          onClick={onAddConditional}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <FiPlus className="w-4 h-4 mr-2" />
          <span>Conditional</span>
        </button>
        <button
          onClick={onAddData}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <FiPlus className="w-4 h-4 mr-2" />
          <span>Data</span>
        </button>

        <button // New button for Agent Node
          onClick={onAddAgent}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <FiPlus className="w-4 h-4 mr-2" />
          <span>Agent</span>
        </button>

        <button
          onClick={onAddToolInput}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <LogIn className="w-4 h-4 mr-2 text-green-400" />
          <span>Tool Input</span>
        </button>
        <button
          onClick={onAddToolOutput}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4 mr-2 text-orange-400" />
          <span>Tool Output</span>
        </button>
        <button
          onClick={onAddRestructure}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <GitMerge className="w-4 h-4 mr-2 text-indigo-400" />
          <span>Restructure</span>
        </button>
        <button // New button for Custom Agent
          onClick={onAddCustomAgent}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <Wand2 className="w-4 h-4 mr-2 text-cyan-400" />
          <span>Custom Agent</span>
        </button>
        <button
          onClick={onAddOperations}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <Calculator className="w-4 h-4 mr-2 text-orange-400" />
          <span>Operations</span>
        </button>
        <button
          onClick={onAddUnwrapArray}
          className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
        >
          <UnfoldVertical className="w-4 h-4 mr-2 text-teal-400" />
          <span>Unwrap Array</span>
        </button>

        <div className="relative">
          <button
            onClick={toggleCustomNodesDropdown}
            className="flex items-center px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded-md transition-colors"
          >
            <FiPlus className="w-4 h-4 mr-2" />
            <span>Custom Nodes</span>
          </button>
          <CustomNodesDropdown
            isOpen={customNodesDropdownOpen}
            onSelect={onCustomNodesSelect}
            onClose={() => { }}
          />
        </div>
      </div> */}
      {/* Center section: Add Node Button */}
      <div className="flex flex-1 items-center justify-center space-x-3">
        <button
          onClick={onOpenAddNodeModal}
          disabled={disabled}
          className="flex items-center rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiPlus className="mr-2 h-4 w-4" />
          <span>Add Node</span>
        </button>
        <button
          onClick={onEditGlobalState}
          className="flex items-center rounded-md bg-zinc-800 px-3 py-1.5 transition-colors hover:bg-zinc-700"
        >
          <Globe className="mr-2 h-4 w-4" />
          <span>Global State</span>
        </button>
      </div>
      {/* Right section with undo/redo and close */}
      <div className="flex items-center space-x-3">
        <div className="mr-4 flex items-center space-x-2">
          <button
            onClick={onUndo}
            className="rounded-md bg-zinc-800 p-1.5 transition-colors hover:bg-zinc-700"
            title="Undo"
          >
            <BiUndo className="h-4 w-4" />
          </button>
          <button
            onClick={onRedo}
            className="rounded-md bg-zinc-800 p-1.5 transition-colors hover:bg-zinc-700"
            title="Redo"
          >
            <BiRedo className="h-4 w-4" />
          </button>
          <button
            onClick={onClear}
            className="rounded-md bg-zinc-800 p-1.5 transition-colors hover:bg-zinc-700"
            title="Clear"
          >
            <AiOutlineClear className="h-4 w-4" />
          </button>
        </div>
        <div className="mr-4 flex items-center space-x-2">
          <button
            disabled={disabled}
            className={`flex items-center rounded-md bg-zinc-800 px-3 py-1.5 transition-colors hover:bg-zinc-700 ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={handleSaveChanges}
          >
            {mode === 'create' ? 'Create Workflow' : 'Save Changes'}
          </button>
          <button
            onClick={onAddAppIntegration}
            className="flex items-center rounded-md bg-zinc-800 px-3 py-1.5 transition-colors hover:bg-zinc-700"
          >
            <FaToolbox className="mr-2 h-4 w-4" />
            <span>Integrations</span>
          </button>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
            title="Close"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
};
